import React, { useContext, useEffect, useState } from 'react';

import { Domain, findSiteByDomain } from '@zooplus/commons-lang';

import Banner from '../../common/components/Banner';
import CustomerBalanceComponent from '../../common/components/CustomerBalance';
import GiftBanner from '../../common/components/GiftBanner';
import ShutdownBanner from '../../common/components/ShutdownBanner';
import SkeletonCustomerBalance from '../../common/components/SkeletonCustomerBalance';
import Constants from '../../common/Constants';
import CartEventsContext from '../../common/contexts/CartEventsContext';
import FeaturesContext from '../../common/contexts/FeaturesContext';
import LocResourcesContext from '../../common/contexts/LocResourcesContext';
import RewardShopContext from '../../common/contexts/RewardShopContext';
import { getLocale } from '../../common/utils/locale';
import { View } from '../../common/utils/types';
import FiltersContext from '../contexts/FiltersContext';

import ArticlesHighlightedCarousel from './articlesHighlighted/ArticlesHighlightedCarousel';
import ArticlesHighlightedList from './articlesHighlighted/ArticlesHighlightedList';
import ArticlesTable from './ArticlesTable';
import DonationCarousel from './donations/DonationCarousel';
import DonationList from './donations/DonationList';
import FiltersMenu from './FiltersMenu';
import ShutdownView from './shutdown/ShutdownView';
import SkeletonShutdown from './shutdown/SkeletonShutdown';
import SkeletonRewardShop from './SkeletonRewardShop';

interface RewardShopComponentProps {
  siteDomain: Domain;
  view: View;
  bpEnabled: boolean;
}

const RewardShopComponent = ({
  view,
  siteDomain,
  bpEnabled,
}: RewardShopComponentProps): JSX.Element => {
  const GLOBAL_PREFIX = 'loy-bp-rws-overview';
  const [isRewardShopComponent, setIsRewardShopComponent] = useState<boolean>(false);
  const [isShutdownComponent, setIsShutdownComponent] = useState<boolean>(false);
  const [isRenderFailed, setIsRenderFailed] = useState<boolean>(false);

  const { showFilters, articlesFiltered, onClearFilters } = useContext(FiltersContext);
  const {
    onSendingMetricEvent,
    allArticles,
    bonusPointsOverview,
    hasAnyRedemption,
    giftStatus,
    giftAmount,
  } = useContext(RewardShopContext);
  const { onFormatLocale, locResources } = useContext(LocResourcesContext);
  const { redeemableBalance } = useContext(CartEventsContext);
  const { features } = useContext(FeaturesContext);
  const site = findSiteByDomain(siteDomain);

  const renderSuccess = () => {
    setIsRenderFailed(false);
    onSendingMetricEvent?.('renderSuccess');
  };

  const renderEmptyArticlesTable = () => {
    return (
      <div className={`${GLOBAL_PREFIX}__empty-table`}>
        <div
          onClick={() => onClearFilters?.()}
          dangerouslySetInnerHTML={{
            __html: onFormatLocale?.('mzp.bp.product.filter.noresult') ?? '',
          }}
        />
      </div>
    );
  };

  const renderNumberOfArticles = (number: number) => {
    return (
      <h3 className={`${GLOBAL_PREFIX}__products-number`} id="loy-rewards-shop-products-number">
        {number} {onFormatLocale?.('loy.rewards.shop.filter.products')}
      </h3>
    );
  };

  const renderCustomerBalance = (): boolean => {
    return (
      features.shutdownLandingPage !== null &&
      view === 'private' &&
      (giftStatus === undefined || giftStatus) &&
      (redeemableBalance ?? 0) > 0
    );
  };

  const renderCustomerBalanceSkeleton = (): boolean => {
    return (
      features.shutdownLandingPage === null &&
      view === 'private' &&
      (giftStatus === undefined || giftStatus) &&
      redeemableBalance === null
    );
  };

  const switchView = (): void => {
    const url = new URL(window.location.href);
    if (isShutdownComponent) {
      url.searchParams.delete('shutdown');
    } else {
      url.searchParams.append('shutdown', 'true');
    }
    window.history.replaceState({}, '', url.toString());
    setIsShutdownComponent(!isShutdownComponent);
  };

  const renderGratingInfo = (): boolean => {
    return features.shutdownLandingPage !== null && !features.shutdownLandingPage;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shutdown = urlParams.get('shutdown') === 'true';
    if (shutdown) setIsShutdownComponent(true);
  }, []);

  useEffect(() => {
    if (isRewardShopComponent) {
      renderSuccess();
    }
    const metricsTimeout = setTimeout(() => {
      if (!isRewardShopComponent) setIsRenderFailed(true);
    }, Constants.RENDER_TIMEOUT);
    return function () {
      clearTimeout(metricsTimeout);
    };
  }, [isRewardShopComponent]);

  useEffect(() => {
    const isRewardShopComponent =
      !!allArticles && allArticles.length >= 0 && !!locResources && locResources.length >= 0;
    setIsRewardShopComponent(isRewardShopComponent);
  }, [allArticles, locResources]);

  const rewardShopComponent = () => {
    return (
      <React.Fragment>
        {renderGratingInfo() && (
          <Banner
            siteDomain={siteDomain}
            view={view}
            locale={getLocale(site)}
            defaultTitle={'loy.bp.shop.title'}
            defaultSubtitle={
              view === 'private' ? 'loy.bp.shop.subtitle' : 'loy.bp.shop.public.subtitle'
            }
            bannerName={'rws.jpg'}
            showNewSimplifiedBanner={view === 'private'}
          />
        )}
        {view === 'private' && giftAmount && (
          <GiftBanner
            showRewardShopButton={false}
            giftAmount={giftAmount}
            giftStatus={giftStatus ?? false}
          />
        )}
        {renderCustomerBalanceSkeleton() && <SkeletonCustomerBalance></SkeletonCustomerBalance>}
        {renderCustomerBalance() && bonusPointsOverview && redeemableBalance && (
          <CustomerBalanceComponent
            showRewardShopButton={false}
            bonusPointsOverview={bonusPointsOverview}
            hasAnyRedemption={hasAnyRedemption!}
            isRewardShopPage={true}
            redeemableBalance={redeemableBalance}
            hideExpiryDate={features.shutdownLandingPage}
          />
        )}
        <div
          className={GLOBAL_PREFIX}
          style={{
            minHeight: showFilters === true && articlesFiltered?.length === 0 ? '800px' : '0',
          }}>
          {siteDomain !== 'zooplus.de' &&
            (screen.width < 1024 ? <DonationList /> : <DonationCarousel />)}
          {features.shutdownLandingPage && <ShutdownBanner switchView={switchView} />}
          {!features.shutdownLandingPage && <ArticlesHighlightedList view={view} />}
          {!features.shutdownLandingPage && <ArticlesHighlightedCarousel view={view} />}
          {<FiltersMenu />}
          {articlesFiltered?.length === 0 && renderEmptyArticlesTable()}
          {(articlesFiltered?.length ?? 0) > 0 && renderNumberOfArticles(articlesFiltered!.length)}
          {(articlesFiltered?.length ?? 0) > 0 && <ArticlesTable view={view} />}
          {siteDomain === 'zooplus.de' && (
            <div
              dangerouslySetInnerHTML={{
                __html: onFormatLocale?.('loy.rewards.shop.donate.info') ?? '',
              }}
            />
          )}
        </div>
      </React.Fragment>
    );
  };

  const loadRewardShopView = () => {
    return isRewardShopComponent ? (
      <React.Fragment>{rewardShopComponent()}</React.Fragment>
    ) : isRenderFailed ? (
      <div></div>
    ) : (
      <React.Fragment>
        {view === 'private' && <SkeletonCustomerBalance></SkeletonCustomerBalance>}
        <SkeletonRewardShop></SkeletonRewardShop>
      </React.Fragment>
    );
  };

  const loadShutdownView = () => {
    return isRewardShopComponent && redeemableBalance ? (
      <ShutdownView
        switchView={switchView}
        view={view}
        bonusPointsOverview={bonusPointsOverview}
        redeemableBalance={redeemableBalance}
        bpEnabled={bpEnabled}
      />
    ) : isRenderFailed ? (
      <div></div>
    ) : (
      <React.Fragment>
        <SkeletonShutdown></SkeletonShutdown>
      </React.Fragment>
    );
  };

  return (
    <div style={{ gridColumn: '1 / -1' }}>
      {features.shutdownLandingPage !== null &&
        (features.shutdownLandingPage && isShutdownComponent
          ? loadShutdownView()
          : loadRewardShopView())}
    </div>
  );
};

export default RewardShopComponent;
